/**
 * Query key factory for TanStack Query.
 * Centralized key management for cache invalidation and organization.
 *
 * Usage:
 * - queryKeys.dashboard.metrics(tenantId)
 * - queryKeys.jobs.list({ status: 'open' })
 * - queryKeys.jobs.detail(jobId)
 */
export const queryKeys = {
  // Dashboard
  dashboard: {
    all: ['dashboard'] as const,
    metrics: (tenantId: string) =>
      [...queryKeys.dashboard.all, 'metrics', tenantId] as const,
    compliance: (tenantId: string) =>
      [...queryKeys.dashboard.all, 'compliance', tenantId] as const,
  },

  // Jobs
  jobs: {
    all: ['jobs'] as const,
    lists: () => [...queryKeys.jobs.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.jobs.lists(), filters] as const,
    details: () => [...queryKeys.jobs.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.jobs.details(), id] as const,
  },

  // Insureds
  insureds: {
    all: ['insureds'] as const,
    lists: () => [...queryKeys.insureds.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.insureds.lists(), filters] as const,
    details: () => [...queryKeys.insureds.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.insureds.details(), id] as const,
  },

  // Documents
  documents: {
    all: ['documents'] as const,
    lists: () => [...queryKeys.documents.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.documents.lists(), filters] as const,
    byJob: (jobId: string) =>
      [...queryKeys.documents.all, 'job', jobId] as const,
    byInsured: (insuredId: string) =>
      [...queryKeys.documents.all, 'insured', insuredId] as const,
    detail: (id: string) =>
      [...queryKeys.documents.all, 'detail', id] as const,
    // Documents linked to a job via DocumentJobLink (separate from `byJob`,
    // which returns docs OWNED by the job).
    linkedToJob: (jobId: string) =>
      [...queryKeys.documents.all, 'linked-to-job', jobId] as const,
  },

  // Risk Requirements
  riskRequirements: {
    all: ['riskRequirements'] as const,
    lists: () => [...queryKeys.riskRequirements.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.riskRequirements.lists(), filters] as const,
    detail: (id: string) =>
      [...queryKeys.riskRequirements.all, 'detail', id] as const,
  },

  // Reports
  reports: {
    all: ['reports'] as const,
    report: (reportType: string, filters: Record<string, unknown>) =>
      [...queryKeys.reports.all, reportType, filters] as const,
  },

  // Departments
  departments: {
    all: ['departments'] as const,
    lists: () => [...queryKeys.departments.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.departments.lists(), filters] as const,
    details: () => [...queryKeys.departments.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.departments.details(), id] as const,
  },

  // User Profiles
  userProfiles: {
    all: ['userProfiles'] as const,
    lists: () => [...queryKeys.userProfiles.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.userProfiles.lists(), filters] as const,
    details: () => [...queryKeys.userProfiles.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.userProfiles.details(), id] as const,
  },

  // Tasks
  tasks: {
    all: ['tasks'] as const,
    lists: () => [...queryKeys.tasks.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.tasks.lists(), filters] as const,
    details: () => [...queryKeys.tasks.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.tasks.details(), id] as const,
  },

  // Notifications
  notifications: {
    all: ['notifications'] as const,
    lists: () => [...queryKeys.notifications.all, 'list'] as const,
    list: (params: Record<string, unknown>) =>
      [...queryKeys.notifications.lists(), params] as const,
    details: () => [...queryKeys.notifications.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.notifications.details(), id] as const,
    inApp: () => [...queryKeys.notifications.all, 'in-app'] as const,
    unreadCount: () => [...queryKeys.notifications.all, 'unread-count'] as const,
  },

  // Contacts
  contacts: {
    all: ['contacts'] as const,
    lists: () => [...queryKeys.contacts.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.contacts.lists(), filters] as const,
    details: () => [...queryKeys.contacts.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.contacts.details(), id] as const,
  },

  // Communication Templates
  communicationTemplates: {
    all: ['communicationTemplates'] as const,
    lists: () => [...queryKeys.communicationTemplates.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.communicationTemplates.lists(), filters] as const,
    details: () => [...queryKeys.communicationTemplates.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.communicationTemplates.details(), id] as const,
  },

  // Briefing
  briefing: {
    all: ['briefing'] as const,
    summary: () => [...queryKeys.briefing.all, 'summary'] as const,
  },

  // Tenant Settings
  tenantSettings: {
    all: ['tenantSettings'] as const,
  },

  // User/Tenant
  user: {
    all: ['user'] as const,
    current: () => [...queryKeys.user.all, 'current'] as const,
    profile: () => [...queryKeys.user.all, 'profile'] as const,
  },
} as const;
